<template>
  <div class="service-order-list">
    <KTCodePreview v-bind:title="'Danh sách Phiếu dịch vụ'">
      <template v-slot:toolbar>
        <b-button size="sm" variant="primary" @click="goIntake">
          <i class="fas fa-plus"></i> <b>Tiếp nhận xe</b>
        </b-button>
      </template>

      <template v-slot:preview>
        <!-- Summary cards -->
        <b-row class="so-summary">
          <b-col
            v-for="card in summaryCards"
            :key="card.key"
            cols="6"
            md="2"
            class="mb-2"
          >
            <div
              class="so-card"
              :class="{ active: activeCard === card.key }"
              @click="onCardClick(card)"
            >
              <div class="so-card-count">{{ card.count }}</div>
              <div class="so-card-label">{{ card.label }}</div>
            </div>
          </b-col>
        </b-row>

        <!-- Filter -->
        <div class="so-filter-card">
          <b-row>
            <b-col cols="6" md="3">
              <label class="so-label">Số phiếu</label>
              <b-form-input v-model="filter.orderNo" size="sm" />
            </b-col>
            <b-col cols="6" md="3">
              <label class="so-label">Biển số xe</label>
              <b-form-input v-model="filter.licensePlate" size="sm" />
            </b-col>
            <b-col cols="6" md="3">
              <label class="so-label">Tên KH / SĐT</label>
              <b-form-input v-model="filter.customer" size="sm" />
            </b-col>
            <b-col cols="6" md="3">
              <label class="so-label">Trạng thái</label>
              <b-form-select
                v-model="filter.status"
                size="sm"
                :options="statusOptions"
              />
            </b-col>
            <b-col cols="6" md="3">
              <label class="so-label">Từ ngày</label>
              <b-form-input v-model="filter.fromDate" type="date" size="sm" />
            </b-col>
            <b-col cols="6" md="3">
              <label class="so-label">Đến ngày</label>
              <b-form-input v-model="filter.toDate" type="date" size="sm" />
            </b-col>
            <b-col cols="12" md="6" class="d-flex align-items-end">
              <b-button
                size="sm"
                variant="primary"
                class="mr-2"
                @click="onFilter"
              >
                Lọc
              </b-button>
              <b-button size="sm" variant="secondary" @click="onClearFilter">
                Xoá lọc
              </b-button>
            </b-col>
          </b-row>
        </div>

        <!-- Table -->
        <b-table
          :items="items"
          :fields="fields"
          :busy="loading"
          small
          bordered
          hover
          responsive
          show-empty
          empty-text="Không tìm thấy phiếu nào phù hợp"
          class="mt-3 so-table"
        >
          <template #table-busy>
            <div class="text-center py-4"><b-spinner /></div>
          </template>
          <template #cell(orderNo)="row">
            <a class="so-order-link" @click="goDetail(row.item)">{{
              row.item.orderNo
            }}</a>
          </template>
          <template #cell(createdAt)="row">
            <div>{{ formatDateOnly(row.item.createdAt) }}</div>
            <div class="text-muted small">
              {{ formatTime(row.item.createdAt) }}
            </div>
          </template>
          <template #cell(customer)="row">
            <div>{{ row.item.customerName || '—' }}</div>
            <div class="text-muted small">{{ row.item.customerPhone }}</div>
          </template>
          <template #cell(licensePlate)="row">
            <b-badge v-if="row.item.licensePlate" variant="light">{{
              row.item.licensePlate
            }}</b-badge>
            <span v-else class="text-muted">—</span>
          </template>
          <template #cell(vehicleModel)="row">
            {{ row.item.vehicleModel || '—' }}
          </template>
          <template #cell(totalAfterVAT)="row">
            <div class="so-total-cell">
              <b class="so-total-amount">{{
                formatPrice(row.item.totalAfterVAT)
              }}</b>
              <p
                v-if="row.item.totalDiscount > 0"
                v-b-tooltip.hover
                title="Chiết khấu đã áp dụng"
                class="so-total-discount"
              >
                ↳ đã giảm {{ formatPrice(row.item.totalDiscount) }}
              </p>
            </div>
          </template>
          <template #cell(assignedSa)="row">
            {{ saName(row.item) }}
          </template>
          <template #cell(status)="row">
            <b-badge :variant="statusVariant(row.item.status)">
              {{ statusLabel(row.item.status) }}
            </b-badge>
          </template>
          <template #cell(actions)="row">
            <b-button
              size="sm"
              variant="link"
              class="p-0 mr-2"
              title="Xem"
              @click="goDetail(row.item)"
            >
              <i class="fas fa-eye"></i>
            </b-button>
            <b-button
              v-if="canEditRow(row.item)"
              size="sm"
              variant="link"
              class="p-0 text-primary"
              title="Sửa báo giá"
              @click="goQuote(row.item)"
            >
              <i class="fas fa-edit"></i>
            </b-button>
          </template>
        </b-table>

        <div class="d-flex justify-content-between align-items-center mt-2">
          <span class="text-muted small">Tổng số: {{ total }} phiếu</span>
          <b-pagination
            v-if="numberOfPage >= 2"
            v-model="page"
            :total-rows="total"
            :per-page="size"
            size="sm"
            class="mb-0"
            @change="onPageChange"
          />
        </div>
      </template>
    </KTCodePreview>
  </div>
</template>

<script>
import moment from 'moment';
import { get } from 'lodash';
import KTCodePreview from '@/view/content/CodePreview.vue';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import ServiceOrderService from '@/core/services/serviceOrder.service';
import {
  SO_STATUS,
  SO_STATUS_LABEL,
  SO_STATUS_VARIANT,
  SO_STATUS_OPTIONS,
} from '@/core/constants/serviceOrder';
import { convertPrice, makeToastFaile } from '@/utils/common';
import { checkPermissions } from '@/utils/saveDataToLocal';

// Trạng thái còn ở giai đoạn báo giá → cho mở màn Báo giá (SO-002).
const QUOTE_STATUSES = [SO_STATUS.QUOTING, SO_STATUS.QUOTED];

// Map nhóm card → key summary BE (báo giá/sửa chữa/quyết toán/xuất xưởng/huỷ).
const CARD_DEFS = [
  { key: 'all', label: 'Tất cả', statuses: null },
  { key: 'baoGia', label: 'Báo giá', statuses: ['QUOTING', 'QUOTED'] },
  {
    key: 'suaChua',
    label: 'Sửa chữa',
    statuses: ['REPAIRING', 'PENDING_PARTS', 'PARTS_ISSUED'],
  },
  {
    key: 'quyetToan',
    label: 'Quyết toán',
    statuses: ['INSPECTING', 'INSPECTED', 'SETTLING', 'SETTLED'],
  },
  { key: 'xuatXuong', label: 'Xuất xưởng', statuses: ['RELEASED'] },
  { key: 'huy', label: 'Huỷ', statuses: ['CANCELLED'] },
];

export default {
  name: 'ServiceOrderList',
  components: { KTCodePreview },
  data() {
    return {
      loading: false,
      items: [],
      total: 0,
      page: parseInt(this.$route.query.page, 10) || 1,
      size: 20,
      activeCard: 'all',
      summary: {},
      filter: {
        orderNo: '',
        licensePlate: '',
        customer: '',
        status: null,
        fromDate: '',
        toDate: '',
      },
      statusOptions: [{ value: null, text: 'Tất cả' }, ...SO_STATUS_OPTIONS],
      fields: [
        { key: 'orderNo', label: 'Số phiếu', thStyle: { width: '130px' } },
        { key: 'createdAt', label: 'Ngày tạo', thStyle: { width: '110px' } },
        { key: 'customer', label: 'Khách hàng', thStyle: { minWidth: '160px' } },
        {
          key: 'licensePlate',
          label: 'Biển số',
          thStyle: { width: '110px' },
          class: 'text-center',
        },
        { key: 'vehicleModel', label: 'Kiểu xe', thStyle: { minWidth: '120px' } },
        {
          key: 'totalAfterVAT',
          label: 'Tổng tiền',
          class: 'text-right',
          thStyle: { width: '150px' },
        },
        { key: 'assignedSa', label: 'SA phụ trách', thStyle: { minWidth: '140px' } },
        {
          key: 'status',
          label: 'Trạng thái',
          class: 'text-center',
          thStyle: { width: '130px' },
        },
        {
          key: 'actions',
          label: 'Thao tác',
          class: 'text-center',
          thStyle: { width: '90px' },
        },
      ],
    };
  },
  computed: {
    numberOfPage() {
      return Math.ceil(this.total / this.size);
    },
    summaryCards() {
      return CARD_DEFS.map((c) => ({
        ...c,
        count: this.summary[c.key] || 0,
      }));
    },
  },
  created() {
    this.loadList();
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [{ title: 'Phiếu dịch vụ' }]);
  },
  methods: {
    statusLabel(s) {
      return SO_STATUS_LABEL[s] || s;
    },
    statusVariant(s) {
      return SO_STATUS_VARIANT[s] || 'secondary';
    },
    formatDateOnly(d) {
      return d ? moment(d).format('DD/MM/YYYY') : '';
    },
    formatTime(d) {
      return d ? moment(d).format('HH:mm') : '';
    },
    formatPrice(v) {
      return convertPrice(v || 0) + ' đ';
    },
    // Tên SA phụ trách — BE join trả assignedSa.fullName. Chưa gán / không có → "—".
    saName(item) {
      return get(item, 'assignedSa.fullName') || '—';
    },
    buildParams() {
      const card = CARD_DEFS.find((c) => c.key === this.activeCard);
      let status;
      if (this.filter.status) {
        status = this.filter.status;
      } else if (card && card.statuses) {
        status = card.statuses;
      }
      return {
        page: this.page,
        limit: this.size,
        orderNo: this.filter.orderNo || undefined,
        licensePlate: this.filter.licensePlate || undefined,
        customer: this.filter.customer || undefined,
        fromDate: this.filter.fromDate || undefined,
        toDate: this.filter.toDate || undefined,
        status,
      };
    },
    async loadList() {
      this.loading = true;
      try {
        const res = await ServiceOrderService.getAll(this.buildParams());
        const inner = get(res, 'data.data', {});
        this.items = inner.data || [];
        this.total = inner.total || 0;
        this.summary = inner.summary || {};
      } catch (e) {
        makeToastFaile('Lỗi tải danh sách');
      } finally {
        this.loading = false;
      }
    },
    onFilter() {
      this.page = 1;
      this.loadList();
    },
    onClearFilter() {
      this.filter = {
        orderNo: '',
        licensePlate: '',
        customer: '',
        status: null,
        fromDate: '',
        toDate: '',
      };
      this.activeCard = 'all';
      this.page = 1;
      this.loadList();
    },
    onCardClick(card) {
      this.activeCard = card.key;
      this.filter.status = null;
      this.page = 1;
      this.loadList();
    },
    onPageChange(p) {
      this.page = p;
      this.$nextTick(() => this.loadList());
    },
    goIntake() {
      this.$router.push({ name: 'intake-service-order' });
    },
    // Số phiếu / 👁 Xem: luôn mở màn chi tiết (workflow SO-003) cho MỌI trạng thái.
    // Sửa nội dung báo giá đi qua ✏️ Sửa → QuotationEdit (chỉ QUOTING/QUOTED).
    goDetail(item) {
      this.$router.push({
        name: 'detail-service-order',
        params: { id: item.id },
      });
    },
    goQuote(item) {
      this.$router.push({
        name: 'quote-service-order',
        params: { id: item.id },
      });
    },
    canEditRow(item) {
      return (
        QUOTE_STATUSES.includes(item.status) &&
        checkPermissions(['SERVICE_ORDER_QUOTE_EDIT'])
      );
    },
  },
};
</script>

<style scoped>
.so-summary {
  margin-bottom: 8px;
}
.so-card {
  background: #fff;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  padding: 10px;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.15s;
}
.so-card.active {
  border-color: #3699ff;
  box-shadow: 0 0 0 1px #3699ff;
}
.so-card-count {
  font-size: 1.25rem;
  font-weight: 700;
  color: #181c32;
}
.so-card-label {
  font-size: 0.8rem;
  color: #7e8299;
}
.so-filter-card {
  background: #f9f9fb;
  border-radius: 6px;
  padding: 12px;
}
.so-label {
  font-size: 0.8rem;
  color: #7e8299;
  margin-bottom: 2px;
}
.so-order-link {
  font-family: monospace;
  color: #3699ff;
  cursor: pointer;
}
/* ---- Bảng danh sách: header nổi + canh giữa dọc, ô nhỏ gọn ---- */
.so-table ::v-deep thead th {
  background: #f3f6f9;
  color: #3f4254;
  font-size: 12px;
  vertical-align: middle;
  white-space: nowrap;
}
.so-table ::v-deep tbody td {
  vertical-align: middle;
  font-size: 13px;
}
/* Cột Tổng tiền — tổng phải trả (xanh đậm) nổi bật trên; dòng "đã giảm" mờ nhỏ bên dưới. */
.so-total-cell {
  text-align: right;
  white-space: nowrap;
}
.so-total-amount {
  color: #2e7d32;
  font-weight: 700;
}
.so-total-discount {
  margin: 2px 0 0;
  font-size: 0.75rem;
  color: #b5b5c3;
}
</style>
